import { z } from 'zod';
import { nameSchema } from './common.js';

/**
 * Email validation
 */
export const emailSchema = z.string().email().max(255).toLowerCase().trim();

/**
 * Password requirements
 */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must be at most 128 characters');

/**
 * User registration (if we support email/password)
 */
export const registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  name: nameSchema,
});

export type RegisterInput = z.infer<typeof registerSchema>;

/**
 * User login
 */
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Request password reset
 */
export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/**
 * Reset password with token
 */
export const resetPasswordSchema = z.object({
  token: z.string().min(1),
  password: passwordSchema,
});

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/**
 * Change password (for logged-in users)
 */
export const changePasswordSchema = z.object({
  currentPassword: z.string().optional(), // Optional because social auth users might not have a password
  newPassword: passwordSchema,
});

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/**
 * Create a new tenant (organization)
 */
export const createTenantSchema = z.object({
  name: nameSchema,
});

export type CreateTenantInput = z.infer<typeof createTenantSchema>;

/**
 * Refresh token request
 */
export const refreshTokenSchema = z.object({
  refreshToken: z.string().min(1, 'Refresh token is required'),
});

export type RefreshTokenInput = z.infer<typeof refreshTokenSchema>;

/**
 * OAuth callback (used internally)
 */
export const oauthCallbackSchema = z.object({
  code: z.string().min(1),
  state: z.string().optional(),
});

export type OAuthCallbackInput = z.infer<typeof oauthCallbackSchema>;

/**
 * Disconnect OAuth account
 */
export const disconnectOAuthSchema = z.object({
  provider: z.enum(['google', 'microsoft']),
});

export type DisconnectOAuthInput = z.infer<typeof disconnectOAuthSchema>;

/**
 * OAuth profile validation
 */
export const oauthProfileSchema = z.object({
  email: emailSchema,
  name: z.string().min(1).max(255).trim(),
  avatarUrl: z
    .string()
    .url()
    .max(2048)
    .refine(
      (url) => {
        try {
          const parsed = new URL(url);
          return ['http:', 'https:'].includes(parsed.protocol);
        } catch {
          return false;
        }
      },
      { message: 'Avatar URL must use HTTP or HTTPS protocol' }
    )
    .optional(),
});

export type OAuthProfile = z.infer<typeof oauthProfileSchema>;

/**
 * OAuth exchange token request
 */
export const oauthExchangeSchema = z.object({
  exchangeToken: z.string().uuid(),
});

export type OAuthExchangeInput = z.infer<typeof oauthExchangeSchema>;
